"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type {
  CShopCartConfig,
  CShopCartProduct,
  CShopCartProductVariant,
  CShopCartTotal,
  CShopCategoryContent,
  CShopContent,
  CShopDeliveryOption,
  CShopOrderContent,
  CShopOrderStep,
  CShopOrderUserData,
  CShopPaymentOption,
} from "@/lib/api/cms-catalog";
import { toast } from "@/lib/notifications/toast";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import { Icon } from "@/lib/icons/Icon";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { CShopCategory } from "@/components/catalogs/CShopCategory/CShopCategory";
import {
  Field,
  Input,
  Select,
  Checkbox,
  FormStatus,
  SubmitButton,
} from "@/components/ui/Form";
import { type CShopTranslations } from "./CShop.i18n";
import styles from "./CShop.module.css";
import { CShopCart } from "./CShopCart";
import { CShopOrder } from "./CShopOrder";
import { CShopOrderComplete } from "./CShopOrderComplete";

/**
 * Feature type z legacy Imperial M_SHOP_OPTIONS - "kolor / tekstura". Wartosc
 * uzywana do wyboru rendera wariantu (swatch vs. tekst).
 */
const FEATURE_TYPE_COLOR_OR_TEXTURE = 3;

/**
 * SHOP_DISCOUNT_CODES_STATUS_* z legacy `web/define.php`. Backend zwraca te
 * kody w `discount_code_status.status` po sprawdzeniu kodu.
 */
const DISCOUNT_STATUS = {
  EMPTY: 0,
  OK: 1,
  NOT_EXISTS: 2,
  EXHAUSTED: 3,
  USED: 4,
  EXPIRED: 5,
} as const;

/**
 * CShop - dispatcher katalogu `c_shop` (root sklepu).
 *
 * Router po `content.view`:
 *   - `card`  - pelna strona koszyka = KROK 1 flow zamawiania (`CShopCart`)
 *   - `order` - formularz danych zamawiajacego = KROK 2 (`CShopOrder`)
 *   - `list`/`article`/`article_not_found` - deleguje do `CShopCategory`
 *     (backend `C_SHOP_CONTROLLER::getApiContent` fallback'uje na
 *     `C_SHOP_CATEGORY_CONTROLLER` dla defaultAction - shape jest identyczny
 *     jak dla katalogu c_shop_category)
 */
export function CShop({ content }: CShopProps) {
  if (content.view === "card") {
    return <CShopCart content={content} />;
  }
  if (content.view === "order") {
    return <CShopOrder content={content} />;
  }
  if (content.view === "order-complete") {
    return <CShopOrderComplete content={content} />;
  }
  /* view: list | article | article_not_found - shape identyczny z katalogiem
     c_shop_category (backend `C_SHOP_CONTROLLER::getApiContent` deleguje do
     `C_SHOP_CATEGORY_CONTROLLER::getApiContent`). */
  return <CShopCategory content={content as CShopCategoryContent} />;
}

export type CShopProps = {
  content: CShopContent;
};

/* ─── Wskaznik krokow zamawiania (breadcrumb-like) ────────────────────── */

export function OrderSteps({ steps }: { steps: CShopOrderStep[] }) {
  return (
    <ol className={styles.steps}>
      {steps.map((step) => {
        const href = step.href !== null ? extractInternalHref(step.href) : null;
        const label = (
          <>
            <span className={styles.stepNr}>{step.nr}</span>
            <span className={styles.stepName}>{step.name}</span>
          </>
        );
        return (
          <li
            key={step.nr}
            className={styles.step}
            data-active={step.active ? "true" : "false"}
            aria-current={step.active ? "step" : undefined}
          >
            {href !== null && !step.active ? (
              <Link href={href} className={styles.stepLink}>
                {label}
              </Link>
            ) : step.href !== null && !step.active ? (
              <a href={step.href} className={styles.stepLink}>
                {label}
              </a>
            ) : (
              <span className={styles.stepLink}>{label}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/* Widoki `CShopCart`, `CShopOrder`, `CShopOrderComplete` przeniesione do
   osobnych plikow (Faza 1 refaktoryzacji). Subkomponenty (OrderSteps, CartLine,
   FreeDeliveryBanner, PaymentOptions, DeliveryOptions, DiscountCode, CartSummary,
   OrderTabsGuest, OrderFormLogged, OrderFormGuest, OrderFormInner, RegulaminLabel)
   zostaja tutaj do Fazy 2 - eksportowane, uzywane przez ekstrahowane widoki. */

/* ─── Tabs dla guesta (rejestracja / just-order / login) ───────────────── */

type GuestTab = "new_client" | "just_order";

export function OrderTabsGuest({
  content,
  t,
}: {
  content: CShopOrderContent;
  t: CShopTranslations;
}) {
  const [activeTab, setActiveTab] = useState<GuestTab>(() =>
    content.is_registration_enabled ? "new_client" : "just_order",
  );

  return (
    <>
      <ul className={styles.tabList} role="tablist">
        {content.is_registration_enabled && (
          <li>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "new_client"}
              data-active={activeTab === "new_client" ? "true" : "false"}
              className={styles.tabButton}
              onClick={() => setActiveTab("new_client")}
            >
              {t.order.tabNewClient}
            </button>
          </li>
        )}
        <li>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "just_order"}
            data-active={activeTab === "just_order" ? "true" : "false"}
            className={styles.tabButton}
            onClick={() => setActiveTab("just_order")}
          >
            {t.order.tabJustOrder}
          </button>
        </li>
        <li>
          <button
            type="button"
            className={styles.tabButton}
            onClick={() =>
              window.dispatchEvent(new CustomEvent("wuseraccount:openlogin"))
            }
          >
            {t.order.tabLogin}
          </button>
        </li>
      </ul>

      <div className={styles.tabPanel} role="tabpanel">
        {activeTab === "new_client" && (
          <OrderFormGuest content={content} type={1} t={t} />
        )}
        {activeTab === "just_order" && (
          <OrderFormGuest content={content} type={2} t={t} />
        )}
      </div>
    </>
  );
}

/* ─── Formularz zamowienia - warianty guest (type=1|2) i logged ────────── */

/* Zod schema builder - dynamiczny wg trybu + konfiguracji checkboxow. Rozne
   pola sa wymagane w roznych trybach (patrz backend validateOrderFields). */
function buildOrderSchema(opts: {
  isLogged: boolean;
  type: 1 | 2;
  requireRegulamin: boolean;
  requirePersonalData: boolean;
  countries: Array<{ code: string; name: string }> | null;
  t: CShopTranslations;
}) {
  const { isLogged, type, requireRegulamin, requirePersonalData, countries, t } = opts;
  const req = (field: string) =>
    z
      .string()
      .trim()
      .min(1, t.order.validationRequired.replace("{field}", field));

  /* Kraj jest wymagany TYLKO gdy sklep ma skonfigurowana liste krajow
     (countries !== null). Whitelist ISO codes wymuszany w superRefine
     ponizej - dodatkowe zabezpieczenie przed manualnym POST z devtoolsow. */
  const countryShape = countries !== null
    ? req(t.order.labelCountry)
    : z.string().optional().default("");
  const billingCountryShape = countries !== null
    ? z.string().optional().default("")  // wymagane tylko gdy fvat=true (superRefine)
    : z.string().optional().default("");

  const shape: Record<string, z.ZodTypeAny> = {
    name: req(t.order.labelName),
    surname: req(t.order.labelSurname),
    telephone: req(t.order.labelTelephone).regex(
      /^[\d +()\-]{6,20}$/,
      t.order.validationTelephone,
    ),
    country: countryShape,
    city: req(t.order.labelCity),
    post_code: req(t.order.labelPostCode),
    street: z.string().optional().default(""),
    nr: req(t.order.labelNr),
    fvat: z.boolean().optional().default(false),
    billing_country: billingCountryShape,
    billing_name: z.string().optional().default(""),
    billing_post_code: z.string().optional().default(""),
    billing_city: z.string().optional().default(""),
    billing_street: z.string().optional().default(""),
    billing_nr: z.string().optional().default(""),
    billing_nip: z.string().optional().default(""),
    marketing: z.boolean().optional().default(false),
  };

  if (!isLogged) {
    shape.email = req(t.order.labelEmail).email(t.order.validationEmail);
    if (type === 1) {
      shape.pass = z
        .string()
        .min(1, t.order.validationRequired.replace("{field}", t.order.labelPass))
        .min(8, t.order.validationPassMinLength);
      shape.ppass = z
        .string()
        .min(1, t.order.validationRequired.replace("{field}", t.order.labelPPass));
    }
    if (requireRegulamin) {
      shape.regulamin = z.literal(true, {
        message: t.order.validationRegulamin,
      });
    } else {
      shape.regulamin = z.boolean().optional().default(false);
    }
    if (requirePersonalData) {
      shape.personal_data = z.literal(true, {
        message: t.order.validationPersonalData,
      });
    } else {
      shape.personal_data = z.boolean().optional().default(false);
    }
  }

  const base = z.object(shape);
  /* Cross-field: fvat -> billing_* required. Cross-field: type=1 pass==ppass.
     Whitelist ISO codes dla country/billing_country gdy sklep obsluguje wiele krajow. */
  return base.superRefine((data, ctx) => {
    if (data.fvat === true) {
      const required: Array<[string, string]> = [
        ["billing_name", t.order.labelCompanyName],
        ["billing_post_code", t.order.labelPostCode],
        ["billing_city", t.order.labelCity],
        ["billing_nr", t.order.labelNr],
      ];
      if (countries !== null) {
        required.push(["billing_country", t.order.labelBillingCountry]);
      }
      for (const [field, label] of required) {
        if (typeof data[field] !== "string" || data[field].trim() === "") {
          ctx.addIssue({
            code: "custom",
            path: [field],
            message: t.order.validationRequired.replace("{field}", label),
          });
        }
      }
    }
    if (countries !== null) {
      const allowed = new Set(countries.map((c) => c.code));
      if (typeof data.country === "string" && data.country !== "" && !allowed.has(data.country)) {
        ctx.addIssue({
          code: "custom",
          path: ["country"],
          message: t.order.validationRequired.replace("{field}", t.order.labelCountry),
        });
      }
      if (
        data.fvat === true &&
        typeof data.billing_country === "string" &&
        data.billing_country !== "" &&
        !allowed.has(data.billing_country)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["billing_country"],
          message: t.order.validationRequired.replace("{field}", t.order.labelBillingCountry),
        });
      }
    }
    if (!isLogged && type === 1) {
      if (data.pass !== data.ppass) {
        ctx.addIssue({
          code: "custom",
          path: ["ppass"],
          message: t.order.validationPassMismatch,
        });
      }
    }
  });
}

/* Formularz dla zalogowanego - prefill z content.user_data, bez checkboxow zgod. */
export function OrderFormLogged({
  content,
  t,
}: {
  content: CShopOrderContent;
  t: CShopTranslations;
}) {
  return (
    <OrderFormInner
      content={content}
      type={2}
      isLogged={true}
      t={t}
    />
  );
}

/* Formularz dla guesta - type decyduje o polach hasla i tekstach submit. */
function OrderFormGuest({
  content,
  type,
  t,
}: {
  content: CShopOrderContent;
  type: 1 | 2;
  t: CShopTranslations;
}) {
  return (
    <OrderFormInner content={content} type={type} isLogged={false} t={t} />
  );
}

type OrderFormValues = {
  name: string;
  surname: string;
  email?: string;
  telephone: string;
  country?: string;
  city: string;
  post_code: string;
  street?: string;
  nr: string;
  pass?: string;
  ppass?: string;
  fvat?: boolean;
  billing_country?: string;
  billing_name?: string;
  billing_post_code?: string;
  billing_city?: string;
  billing_street?: string;
  billing_nr?: string;
  billing_nip?: string;
  regulamin?: boolean;
  personal_data?: boolean;
  marketing?: boolean;
};

function OrderFormInner({
  content,
  type,
  isLogged,
  t,
}: {
  content: CShopOrderContent;
  type: 1 | 2;
  isLogged: boolean;
  t: CShopTranslations;
}) {
  const router = useRouter();
  const [topError, setTopError] = useState<string | null>(null);
  const cardHref = extractInternalHref(content.href.card);

  const requireRegulamin =
    !isLogged && content.order_config.checkbox_accept_regulamin;
  const requirePersonalData =
    !isLogged && content.order_config.show_personal_data_checkbox;
  const showMarketing = content.order_config.marketing_checkbox;

  const schema = useMemo(
    () =>
      buildOrderSchema({
        isLogged,
        type,
        requireRegulamin,
        requirePersonalData,
        countries: content.countries,
        t,
      }),
    [isLogged, type, requireRegulamin, requirePersonalData, content.countries, t],
  );

  const defaults = useMemo<OrderFormValues>(() => {
    const u: CShopOrderUserData | null = content.user_data;
    /* Kraj: gdy sklep obsluguje dokladnie 1 kraj - auto-prefill (pole ukryte).
       Gdy 2+ krajow lub null - pusty string (2+ = user musi wybrac, null = pole nieobecne). */
    const singleCountry =
      content.countries !== null && content.countries.length === 1
        ? content.countries[0].code
        : "";
    return {
      email: u?.email ?? "",
      name: u?.name ?? "",
      surname: u?.surname ?? "",
      telephone: u?.telephone ?? "",
      country: singleCountry,
      city: u?.city ?? "",
      post_code: u?.post_code ?? "",
      street: u?.street ?? "",
      nr: u?.nr ?? "",
      pass: "",
      ppass: "",
      fvat: false,
      billing_country: singleCountry,
      billing_name: u?.billing_name ?? "",
      billing_post_code: u?.billing_post_code ?? "",
      billing_city: u?.billing_city ?? "",
      billing_street: u?.billing_street ?? "",
      billing_nr: u?.billing_nr ?? "",
      billing_nip: u?.billing_nip ?? "",
      regulamin: false,
      personal_data: false,
      marketing: false,
    };
  }, [content.user_data, content.countries]);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OrderFormValues>({
    /* Zod schema jest dynamiczny (isLogged/type/checkboxy) - typowanie
       useForm zostawiamy jako OrderFormValues, resolver bierze zod
       runtime - `unknown` cast tak jak w CContactForm.tsx. */
    resolver: zodResolver(schema) as unknown as never,
    mode: "onTouched",
    defaultValues: defaults,
  });

  const fvatChecked = watch("fvat");

  const onSubmit = async (values: OrderFormValues) => {
    setTopError(null);
    try {
      const res = await fetch("/api/c-shop/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          card_id: content.card_id,
          type,
          ...values,
        }),
      });
      const body = (await res.json().catch(() => null)) as {
        ok?: boolean;
        message?: string;
        errors?: Record<string, string>;
        redirect_url?: string;
      } | null;

      if (!res.ok || body?.ok !== true) {
        if (body?.errors && typeof body.errors === "object") {
          for (const [name, msg] of Object.entries(body.errors)) {
            if (typeof msg === "string") {
              setError(name as keyof OrderFormValues, {
                type: "server",
                message: msg,
              });
            }
          }
          setTopError(body?.message ?? t.order.topError);
          return;
        }
        setTopError(body?.message ?? t.order.topError);
        return;
      }

      /* Sukces - redirect na strone potwierdzenia. Backend zwraca pelen URL
         (moze byc na innej domenie CMS - stad natywny redirect a nie Link). */
      if (typeof body.redirect_url === "string" && body.redirect_url !== "") {
        window.location.href = body.redirect_url;
        return;
      }
      /* Fallback - odswiez strone (backend nie zwrocil URL). */
      router.refresh();
    } catch {
      setTopError(t.order.topError);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className={styles.orderForm}>
      {/* Sekcja 1: dane kontaktowe */}
      <section className={styles.orderSection}>
        <h3 className={styles.orderSectionHeading}>
          <span className={styles.orderSectionNumber}>1</span>
          {t.order.sectionContact}
        </h3>
        <div className={styles.fieldsGrid}>
          <Field label={t.order.labelName} required error={errors.name?.message}>
            <Input type="text" autoComplete="given-name" {...register("name")} />
          </Field>
          <Field label={t.order.labelSurname} required error={errors.surname?.message}>
            <Input type="text" autoComplete="family-name" {...register("surname")} />
          </Field>
          {!isLogged && (
            <Field label={t.order.labelEmail} required error={errors.email?.message}>
              <Input type="email" autoComplete="email" {...register("email")} />
            </Field>
          )}
          <Field label={t.order.labelTelephone} required error={errors.telephone?.message}>
            <Input type="tel" autoComplete="tel" {...register("telephone")} />
          </Field>
          {!isLogged && type === 1 && (
            <>
              <Field label={t.order.labelPass} required error={errors.pass?.message}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  {...register("pass")}
                />
              </Field>
              <Field label={t.order.labelPPass} required error={errors.ppass?.message}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  {...register("ppass")}
                />
              </Field>
            </>
          )}
        </div>
      </section>

      {/* Sekcja 2: adres wysylki */}
      <section className={styles.orderSection}>
        <h3 className={styles.orderSectionHeading}>
          <span className={styles.orderSectionNumber}>2</span>
          {t.order.sectionSendAddress}
        </h3>
        {/* Kraj:
              - content.countries === null -> pole nieobecne (kolumna zostaje pusta,
                admin uzupelnia recznie w panelu);
              - length === 1 -> hidden input (auto-prefill w `defaults`, user nie widzi);
              - length >= 2 -> visible select bez preselekcji (klient musi wybrac). */}
        {content.countries !== null && content.countries.length === 1 && (
          <input type="hidden" {...register("country")} />
        )}
        {content.countries !== null && content.countries.length >= 2 && (
          <Field label={t.order.labelCountry} required error={errors.country?.message}>
            <Select
              autoComplete="country"
              placeholder={t.order.countrySelectPlaceholder}
              {...register("country")}
            >
              {content.countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className={styles.fieldsGridPost}>
          <Field label={t.order.labelPostCode} required error={errors.post_code?.message}>
            <Input type="text" autoComplete="postal-code" {...register("post_code")} />
          </Field>
          <Field label={t.order.labelCity} required error={errors.city?.message}>
            <Input type="text" autoComplete="address-level2" {...register("city")} />
          </Field>
        </div>
        <div className={styles.fieldsGridStreet}>
          <Field label={t.order.labelStreet} error={errors.street?.message}>
            <Input type="text" autoComplete="address-line1" {...register("street")} />
          </Field>
          <Field label={t.order.labelNr} required error={errors.nr?.message}>
            <Input type="text" autoComplete="address-line2" {...register("nr")} />
          </Field>
        </div>
      </section>

      {/* Sekcja 3: dane do faktury (opcjonalne) */}
      <section className={styles.orderSection}>
        <h3 className={styles.orderSectionHeading}>
          <span className={styles.orderSectionNumber}>3</span>
          {t.order.sectionFvatData}
        </h3>
        <Checkbox {...register("fvat")}>{t.order.fvatCheckbox}</Checkbox>
        {fvatChecked && (
          <>
            {/* Kraj do faktury - ta sama lista co dla dostawy. Hidden gdy 1 kraj,
                visible select gdy 2+, brak pola gdy sklep bez skonfigurowanych krajow. */}
            {content.countries !== null && content.countries.length === 1 && (
              <input type="hidden" {...register("billing_country")} />
            )}
            {content.countries !== null && content.countries.length >= 2 && (
              <Field
                label={t.order.labelBillingCountry}
                required
                error={errors.billing_country?.message}
              >
                <Select
                  placeholder={t.order.countrySelectPlaceholder}
                  {...register("billing_country")}
                >
                  {content.countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            <div className={styles.fieldsGrid}>
              <Field
                label={t.order.labelCompanyName}
                required
                error={errors.billing_name?.message}
                className={styles.fieldFull}
              >
                <Input
                  type="text"
                  autoComplete="organization"
                  {...register("billing_name")}
                />
              </Field>
              <Field
                label={t.order.labelPostCode}
                required
                error={errors.billing_post_code?.message}
              >
                <Input type="text" {...register("billing_post_code")} />
              </Field>
              <Field
                label={t.order.labelCity}
                required
                error={errors.billing_city?.message}
              >
                <Input type="text" {...register("billing_city")} />
              </Field>
              <Field label={t.order.labelStreet} error={errors.billing_street?.message}>
                <Input type="text" {...register("billing_street")} />
              </Field>
              <Field
                label={t.order.labelNr}
                required
                error={errors.billing_nr?.message}
              >
                <Input type="text" {...register("billing_nr")} />
              </Field>
              <Field label={t.order.labelNip} error={errors.billing_nip?.message}>
                <Input type="text" {...register("billing_nip")} />
              </Field>
            </div>
          </>
        )}
      </section>

      {/* Sekcja 4: checkboxy zgod (tylko dla niezalogowanego wg legacy) */}
      {!isLogged && (requireRegulamin || requirePersonalData || showMarketing) && (
        <section className={styles.orderSection}>
          <h3 className={styles.orderSectionHeading}>
            <span className={styles.orderSectionNumber}>4</span>
            {t.order.sectionCheckboxData}
          </h3>
          <div className={styles.orderCheckboxRow}>
            {requireRegulamin && (
              <div>
                <Checkbox {...register("regulamin")}>
                  <RegulaminLabel
                    template={t.order.regulaminCheckbox}
                    regulaminAddress={content.order_config.regulamin_address}
                  />
                </Checkbox>
                {errors.regulamin?.message && (
                  <FormStatus variant="error">
                    {errors.regulamin.message}
                  </FormStatus>
                )}
              </div>
            )}
            {requirePersonalData && (
              <div>
                <Checkbox {...register("personal_data")}>
                  {t.order.personalDataCheckbox}
                </Checkbox>
                {errors.personal_data?.message && (
                  <FormStatus variant="error">
                    {errors.personal_data.message}
                  </FormStatus>
                )}
              </div>
            )}
            {showMarketing && (
              <Checkbox {...register("marketing")}>
                {formatWithHost(t.order.marketingCheckbox)}
              </Checkbox>
            )}
          </div>
        </section>
      )}

      {topError !== null && (
        <FormStatus variant="error">{topError}</FormStatus>
      )}

      <div className={styles.orderFormActions}>
        <div className={styles.orderFormActionsLeft}>
          {cardHref !== null ? (
            <Link href={cardHref} className={styles.buttonSecondary}>
              {t.order.backToCard}
            </Link>
          ) : (
            <a href={content.href.card} className={styles.buttonSecondary}>
              {t.order.backToCard}
            </a>
          )}
        </div>
        <div className={styles.orderFormActionsRight}>
          <SubmitButton
            loading={isSubmitting}
            loadingLabel={t.order.submitBusy}
          >
            {t.order.submit}
          </SubmitButton>
        </div>
      </div>
    </form>
  );
}

/* ─── Helpery renderu tekstu z markdown-linkiem `[label](href)` ────────── */

/** Podstawia `{host}` w tlumaczeniu na aktualny `window.location.host` (client). */
function formatWithHost(template: string): string {
  const host = typeof window !== "undefined" ? window.location.host : "";
  return template.replace("{host}", host);
}

/**
 * Renderuje szablon regulaminu (`"Oswiadczam ze [regulaminem]({url}) serwisu
 * {host}..."`) - jeden `[text](url)` fragment + `{regulamin_address}` +
 * `{host}` placeholdery. Bez uzycia dangerouslySetInnerHTML - splitujemy
 * na fragmenty i renderujemy natywny `<a>` w linku.
 */
function RegulaminLabel({
  template,
  regulaminAddress,
}: {
  template: string;
  regulaminAddress: string;
}) {
  const withHost = formatWithHost(
    template.replace("{regulamin_address}", regulaminAddress),
  );

  /* Bez flagi `s` - explicit `[\s\S]` zeby dopasowac newliny w tresci. */
  const match = withHost.match(/^([\s\S]*?)\[([^\]]+)\]\(([^)]+)\)([\s\S]*)$/);
  if (match === null) {
    return <>{withHost}</>;
  }
  const [, before, linkText, linkHref, after] = match;
  return (
    <>
      {before}
      <a href={linkHref} target="_blank" rel="noopener noreferrer">
        {linkText}
      </a>
      {after}
    </>
  );
}

/* ─── Pojedyncza pozycja koszyka (interaktywna) ───────────────────────── */

export function CartLine({
  product,
  currency,
  busy,
  onChangeCount,
  onDelete,
  t,
}: {
  product: CShopCartProduct;
  currency: string;
  busy: boolean;
  onChangeCount: (cId: number, count: number) => void | Promise<void>;
  onDelete: (cId: number) => void | Promise<void>;
  t: CShopTranslations;
}) {
  const productHref = extractInternalHref(product.href);
  const lineBrutto = product.price_brutto * product.count;

  const [inputValue, setInputValue] = useState(String(product.count));
  useEffect(() => {
    setInputValue(String(product.count));
  }, [product.count]);

  const clampAndCommit = useCallback(
    (rawValue: string) => {
      const parsed = Number.parseInt(rawValue, 10);
      const min = 1;
      const max = product.stock > 0 ? product.stock : min;
      const clamped = Number.isFinite(parsed)
        ? Math.max(min, Math.min(max, parsed))
        : min;
      setInputValue(String(clamped));
      if (clamped !== product.count) {
        void onChangeCount(product.c_id, clamped);
      }
    },
    [onChangeCount, product.c_id, product.count, product.stock],
  );

  const decDisabled = busy || product.count <= 1;
  const incDisabled = busy || product.count >= product.stock;

  return (
    <li className={`${styles.item} ${busy ? styles.itemBusy : ""}`}>
      <div className={styles.thumb}>
        {product.picture?.file ? (
          productHref !== null ? (
            <Link href={productHref} tabIndex={-1}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={product.picture.file} alt="" loading="lazy" />
            </Link>
          ) : (
            <a href={product.href} tabIndex={-1}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={product.picture.file} alt="" loading="lazy" />
            </a>
          )
        ) : (
          <ImagePlaceholder />
        )}
      </div>

      <div className={styles.details}>
        <h2 className={styles.name}>
          {productHref !== null ? (
            <Link href={productHref}>{product.name}</Link>
          ) : (
            <a href={product.href}>{product.name}</a>
          )}
        </h2>

        <div className={styles.meta}>
          <span className={styles.metaItem}>
            {t.cart.productId}: <strong>{product.id}</strong>
          </span>
          <span className={styles.metaItem}>
            {t.cart.productPrice}:{" "}
            {product.promocja && product.price_brutto_before_promotion !== null && (
              <span
                className={styles.metaPriceBefore}
                aria-label={t.cart.productPriceBeforePromotion}
              >
                {t.formatPrice(product.price_brutto_before_promotion, currency)}
              </span>
            )}
            <strong>{t.formatPrice(product.price_brutto, currency)}</strong>
          </span>
        </div>

        {product.variant.length > 0 && (
          <div className={styles.variants}>
            <span>{t.cart.productVariant}:</span>
            {product.variant.map((v, idx) => (
              <CartLineVariant key={idx} variant={v} t={t} />
            ))}
          </div>
        )}

        <div className={styles.actionsRow}>
          <div className={styles.stepper} aria-label={t.cart.countLabel}>
            <button
              type="button"
              className={styles.stepperBtn}
              onClick={() => void onChangeCount(product.c_id, product.count - 1)}
              disabled={decDisabled}
              aria-label={t.cart.countDecrement}
            >
              <Icon name="minus" />
            </button>
            <input
              className={styles.stepperInput}
              type="number"
              inputMode="numeric"
              min={1}
              max={product.stock}
              value={inputValue}
              disabled={busy}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={(e) => clampAndCommit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  clampAndCommit((e.target as HTMLInputElement).value);
                }
              }}
              aria-label={t.cart.countLabel}
            />
            <button
              type="button"
              className={styles.stepperBtn}
              onClick={() => void onChangeCount(product.c_id, product.count + 1)}
              disabled={incDisabled}
              aria-label={t.cart.countIncrement}
            >
              <Icon name="plus" />
            </button>
          </div>

          <div className={styles.lineSum}>
            <span className={styles.lineSumLabel}>{t.cart.productSum}:</span>
            <span className={styles.lineSumValue}>
              {t.formatPrice(lineBrutto, currency)}
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={styles.deleteBtn}
        onClick={() => void onDelete(product.c_id)}
        disabled={busy}
        aria-label={t.cart.deleteItem}
      >
        <Icon name="trash" />
      </button>
    </li>
  );
}

/* ─── Wariant pozycji (kolor swatch / tekstura / tekst) ───────────────── */

function CartLineVariant({
  variant,
  t,
}: {
  variant: CShopCartProductVariant;
  t: CShopTranslations;
}) {
  const isColorOrTexture = variant.feature_type === FEATURE_TYPE_COLOR_OR_TEXTURE;

  return (
    <span className={styles.variantRow}>
      <span>{variant.feature_name}:</span>
      {isColorOrTexture ? (
        variant.feature_value_texture ? (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            className={styles.variantSwatchImg}
            src={variant.feature_value_texture}
            alt={t.cart.variantColorAlt}
          />
        ) : variant.feature_value_color ? (
          <span
            className={styles.variantSwatch}
            style={{ background: `#${variant.feature_value_color}` }}
            aria-hidden="true"
          />
        ) : null
      ) : null}
      <span className={styles.variantValueName}>
        {variant.feature_value_name}
      </span>
    </span>
  );
}

/* ─── Baner darmowej dostawy (prog / progres / gratulacje) ────────────── */

export function FreeDeliveryBanner({
  threshold,
  isFree,
  remaining,
  currency,
  t,
}: {
  threshold: number | null;
  isFree: boolean;
  remaining: number | null;
  currency: string;
  t: CShopTranslations;
}) {
  if (threshold === null || threshold <= 0) return null;

  const status = isFree ? (
    <span className={styles.freeDeliveryOk}>{t.freeDelivery.is}</span>
  ) : (
    <span className={styles.freeDeliveryPending}>
      {t.freeDelivery.remaining
        .replace("{remaining}", t.formatPrice(remaining ?? 0, ""))
        .replace("{currency}", currency)}
    </span>
  );

  return (
    <div className={styles.freeDeliveryBanner}>
      <div className={styles.freeDeliveryThreshold}>
        {t.freeDelivery.threshold
          .replace("{price}", t.formatPrice(threshold, ""))
          .replace("{currency}", currency)}
      </div>
      {status}
    </div>
  );
}

/* ─── Opcje platnosci (radio) ─────────────────────────────────────────── */

export function PaymentOptions({
  cardId,
  options,
  selectedId,
  currency,
  onChanged,
  t,
}: {
  cardId: number | null;
  options: CShopPaymentOption[];
  selectedId: number | null;
  currency: string;
  onChanged: (
    config: CShopCartConfig,
    total: CShopCartTotal,
    deliveryOptions: CShopDeliveryOption[] | null,
  ) => void;
  t: CShopTranslations;
}) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  const pick = useCallback(
    async (id: number) => {
      if (cardId === null || busy) return;
      setBusy(true);
      try {
        const res = await fetch("/api/c-shop/update-card", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            card_id: cardId,
            payment_option_id: id,
          }),
        });
        if (!res.ok) throw new Error("update failed");
        const data = (await res.json()) as UpdateCardResponse;
        if (!data.ok) throw new Error("update failed");
        onChanged(data.config, data.total, data.delivery_options ?? []);
        router.refresh();
      } catch {
        toast.error(t.payment.changeFailed);
      } finally {
        setBusy(false);
      }
    },
    [busy, cardId, onChanged, router, t.payment.changeFailed],
  );

  return (
    <section className={styles.optionsBlock} aria-busy={busy}>
      <h3 className={styles.optionsHeading}>{t.payment.heading}</h3>
      {options.length === 0 ? (
        <p className={styles.optionsEmpty}>{t.payment.emptyList}</p>
      ) : (
        <ul className={styles.optionsList}>
          {options.map((opt) => {
            const isChecked = selectedId === opt.id;
            /* disabled_over_max: opcja platnosci online, gdy total > limit
               (m_cms_domains > shop_online_payment_max_amount per waluta).
               Tooltip pod hover + aria-label dla screen readerow. */
            const overMaxHint =
              opt.disabled && opt.disabled_over_max !== null
                ? t.payment.disabledOverMax
                    .replace("{amount}", t.formatPrice(opt.disabled_over_max, ""))
                    .replace("{currency}", currency)
                : null;
            return (
              <li key={opt.id} className={styles.optionsItem}>
                <label
                  className={styles.optionsLabel}
                  title={overMaxHint ?? undefined}
                >
                  <input
                    type="radio"
                    name="c_shop_payment_option"
                    value={opt.id}
                    checked={isChecked}
                    disabled={busy || opt.disabled}
                    aria-describedby={
                      overMaxHint !== null
                        ? `c-shop-payment-hint-${opt.id}`
                        : undefined
                    }
                    onChange={() => void pick(opt.id)}
                  />
                  <span>{opt.name}</span>
                </label>
                {overMaxHint !== null && (
                  <div
                    id={`c-shop-payment-hint-${opt.id}`}
                    className={styles.optionsInfo}
                  >
                    {overMaxHint}
                  </div>
                )}
                {isChecked && opt.additional_info && (
                  <div className={styles.optionsInfo}>{opt.additional_info}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ─── Opcje dostawy (radio, zalezne od platnosci) ─────────────────────── */

export function DeliveryOptions({
  cardId,
  options,
  selectedId,
  hasPayment,
  currency,
  onChanged,
  t,
}: {
  cardId: number | null;
  options: CShopDeliveryOption[];
  selectedId: number | null;
  hasPayment: boolean;
  currency: string;
  onChanged: (config: CShopCartConfig, total: CShopCartTotal) => void;
  t: CShopTranslations;
}) {
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  const pick = useCallback(
    async (id: number) => {
      if (cardId === null || busy) return;
      setBusy(true);
      try {
        const res = await fetch("/api/c-shop/update-card", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            card_id: cardId,
            delivery_option_id: id,
          }),
        });
        if (!res.ok) throw new Error("update failed");
        const data = (await res.json()) as UpdateCardResponse;
        if (!data.ok) throw new Error("update failed");
        onChanged(data.config, data.total);
        router.refresh();
      } catch {
        toast.error(t.delivery.changeFailed);
      } finally {
        setBusy(false);
      }
    },
    [busy, cardId, onChanged, router, t.delivery.changeFailed],
  );

  return (
    <section className={styles.optionsBlock} aria-busy={busy}>
      <h3 className={styles.optionsHeading}>{t.delivery.heading}</h3>
      {!hasPayment ? (
        <p className={styles.optionsHint}>{t.delivery.pickPaymentFirst}</p>
      ) : options.length === 0 ? (
        <p className={styles.optionsEmpty}>{t.delivery.emptyList}</p>
      ) : (
        <ul className={styles.optionsList}>
          {options.map((opt) => {
            const isChecked = selectedId === opt.id;
            const priceLabel =
              opt.price_brutto > 0
                ? t.formatPrice(opt.price_brutto, currency)
                : t.delivery.free;
            return (
              <li key={opt.id} className={styles.optionsItem}>
                <label className={styles.optionsLabel}>
                  <input
                    type="radio"
                    name="c_shop_delivery_option"
                    value={opt.id}
                    checked={isChecked}
                    disabled={busy}
                    onChange={() => void pick(opt.id)}
                  />
                  <span className={styles.optionsLabelName}>{opt.name}</span>
                  <span className={styles.optionsLabelPrice}>{priceLabel}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

/* ─── Kod rabatowy ────────────────────────────────────────────────────── */

export function DiscountCode({
  cardId,
  currentCode,
  hasApplied,
  onChanged,
  t,
}: {
  cardId: number | null;
  currentCode: string;
  hasApplied: boolean;
  onChanged: (config: CShopCartConfig, total: CShopCartTotal) => void;
  t: CShopTranslations;
}) {
  const router = useRouter();
  const [input, setInput] = useState(currentCode);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{
    kind: "ok" | "error";
    text: string;
  } | null>(null);

  /* Rerender'y po router.refresh() moga dostarczyc nowy `currentCode` (np.
     inny user w innej karcie) - synchronizujemy lokalny input tylko gdy sam
     user nie edytuje. useRef sygnalizuje ostatni "wysrodkowany" stan. */
  const lastSynced = useRef(currentCode);
  useEffect(() => {
    if (input === lastSynced.current) {
      setInput(currentCode);
      lastSynced.current = currentCode;
    }
  }, [currentCode, input]);

  const messageForStatus = useCallback(
    (status: number): { kind: "ok" | "error"; text: string } | null => {
      switch (status) {
        case DISCOUNT_STATUS.OK:
          return { kind: "ok", text: t.discount.statusOk };
        case DISCOUNT_STATUS.NOT_EXISTS:
          return { kind: "error", text: t.discount.statusNotExists };
        case DISCOUNT_STATUS.EXPIRED:
          return { kind: "error", text: t.discount.statusExpired };
        case DISCOUNT_STATUS.USED:
          return { kind: "error", text: t.discount.statusUsed };
        case DISCOUNT_STATUS.EXHAUSTED:
          return { kind: "error", text: t.discount.statusExhausted };
        default:
          return null;
      }
    },
    [t.discount],
  );

  const submit = useCallback(
    async (code: string) => {
      if (cardId === null || busy) return;
      setBusy(true);
      setStatusMsg(null);
      try {
        const res = await fetch("/api/c-shop/update-card", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            card_id: cardId,
            discount_code: code,
          }),
        });
        if (!res.ok) throw new Error("update failed");
        const data = (await res.json()) as UpdateCardResponse;
        if (!data.ok) throw new Error("update failed");
        onChanged(data.config, data.total);
        lastSynced.current = data.config.discount_code.code;
        setInput(data.config.discount_code.code);
        if (data.discount_code_status) {
          setStatusMsg(messageForStatus(data.discount_code_status.status));
        }
        router.refresh();
      } catch {
        setStatusMsg({ kind: "error", text: t.discount.checkFailed });
      } finally {
        setBusy(false);
      }
    },
    [busy, cardId, messageForStatus, onChanged, router, t.discount.checkFailed],
  );

  return (
    <section className={styles.discountBlock} aria-busy={busy}>
      <h3 className={styles.optionsHeading}>{t.discount.heading}</h3>
      <div className={styles.discountRow}>
        <input
          className={styles.discountInput}
          type="text"
          placeholder={t.discount.placeholder}
          value={input}
          disabled={busy}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void submit(input);
            }
          }}
          aria-label={t.discount.heading}
        />
        <button
          type="button"
          className={styles.buttonSecondary}
          disabled={busy || input.trim() === ""}
          onClick={() => void submit(input)}
        >
          {t.discount.apply}
        </button>
        {hasApplied && (
          <button
            type="button"
            className={styles.discountRemoveBtn}
            disabled={busy}
            onClick={() => void submit("")}
          >
            {t.discount.remove}
          </button>
        )}
      </div>
      <p className={styles.discountHint}>{t.discount.hint}</p>
      {statusMsg !== null && (
        <p
          className={
            statusMsg.kind === "ok"
              ? styles.discountStatusOk
              : styles.discountStatusError
          }
          role="status"
        >
          {statusMsg.text}
        </p>
      )}
    </section>
  );
}

/* ─── Podsumowanie koszyka (sztuki + produkty + dostawa + znizka + razem) ─ */

export function CartSummary({
  total,
  currency,
  t,
}: {
  total: CShopCartTotal;
  currency: string;
  t: CShopTranslations;
}) {
  const hasDiscount = total.discount_brutto > 0;
  const hasDelivery = total.delivery_brutto > 0;

  return (
    <div className={styles.summary}>
      <h3 className={styles.summaryTitle}>{t.cart.summary}</h3>
      <p className={styles.summaryVatNote}>{t.cart.pricesIncludeVat}</p>
      <div className={styles.summaryRow}>
        <span>{t.cart.sumCount}:</span>
        <span className={styles.summaryValue}>{total.count}</span>
      </div>
      <div className={styles.summaryRow}>
        <span>{t.cart.sumProducts}:</span>
        <span className={styles.summaryValue}>
          {t.formatPrice(total.price_brutto, currency)}
        </span>
      </div>
      {hasDelivery && (
        <div className={styles.summaryRow}>
          <span>{t.cart.sumDelivery}:</span>
          <span className={styles.summaryValue}>
            {t.formatPrice(total.delivery_brutto, currency)}
          </span>
        </div>
      )}
      {hasDiscount && (
        <div className={styles.summaryRow}>
          <span>{t.cart.discount}:</span>
          <span className={styles.summaryDiscountValue}>
            -{t.formatPrice(total.discount_brutto, currency)}
          </span>
        </div>
      )}
      <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
        <span>{t.cart.total}:</span>
        <span className={styles.summaryTotalValue}>
          {t.formatPrice(total.total_brutto, currency)}
        </span>
      </div>
    </div>
  );
}

/* ─── Response shape z `/api/c-shop/update-card` ──────────────────────── */

type UpdateCardResponse = {
  ok: boolean;
  config: CShopCartConfig;
  total: CShopCartTotal;
  delivery_options?: CShopDeliveryOption[];
  discount_code_status?: {
    status: number;
    info: string | null;
  };
};